import { Button } from "@/app/global-components/button";
import { CardContent } from "@/app/global-components/card";
import { Card } from "@/app/global-components/card";
import { Input } from "@/app/global-components/input";
import { cn } from "@/lib/utils";
import React, { useState } from "react";
import { BiHeart } from "react-icons/bi";
import { FaStore } from "react-icons/fa";
import { TbTargetArrow } from "react-icons/tb";
import { BiBasket } from "react-icons/bi";
import { BsFillBasketFill } from "react-icons/bs";
import { Tooltip } from "@mui/material";
import { useTranslate } from "@/locales";
import Slider from "react-slick";
import { useLocale } from "@/middleware";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { createContext, useContext, useEffect } from "react";
import { useCart } from "@/contexts/CartContext";
import { Star } from "lucide-react";

interface Product {
  id: string;
  name: string;
  categories: string[];
  companyId: string;
  store: {
    name: string;
    city?: string;
    region?: string;
  };
  price: number;
  minOrder: number;
  unit: string;
  images: string[];
  isFavorite?: boolean;
  quantity?: number;
}

interface FavoritesContextType {
  favorites: string[];
  toggleFavorite: (id: string) => void;
}

const FavoritesContext = createContext<FavoritesContextType>({
  favorites: [],
  toggleFavorite: () => {},
});

function ProductCard({
  product,
  viewMode,
}: {
  product: Product;
  viewMode: "grid" | "list";
}) {
  const { favorites, toggleFavorite } = useContext(FavoritesContext);
  const [isFavorite, setIsFavorite] = useState(favorites.includes(product.id));
  const [quantity, setQuantity] = useState(
    product.minOrder == 0 ? 1 : product.minOrder
  );
  const navigate = useNavigate();
  const {
    addToCart,
    removeFromCart,
    cartItems,
    updateQuantity,
    addToInquiry,
    removeFromInquiry,
    isInInquiry,
  } = useCart();
  const isInCart = cartItems.some((item) => item.id === product.id);
  const cartItem = cartItems.find((item) => item.id === product.id);
  const inInquiry = isInInquiry(product.id);

  useEffect(() => {
    if (cartItem) {
      setQuantity(cartItem.quantity);
    } else {
      setQuantity(product.minOrder == 0 ? 1 : product.minOrder);
    }
  }, [cartItem, product.minOrder]);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.split("")[0] === "0") {
      setQuantity(parseInt(e.target.value.split("")[1]));
      return;
    }
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= product.minOrder) {
      setQuantity(value);
      if (isInCart) {
        updateQuantity(product.id, value);
      }
    }
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    if (isInCart) {
      updateQuantity(product.id, newQuantity);
    }
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (quantity <= product.minOrder || isInCart) {
      return null;
    }
    if (quantity > product.minOrder) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      if (isInCart) {
        updateQuantity(product.id, newQuantity);
      }
    }
  };

  const handleCartToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isInCart) {
      removeFromCart(product.id);
      setQuantity(product.minOrder);
      toast.warning("Product removed from cart", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } else {
      const productWithQuantity = {
        ...product,
        categories: product.categories,
        companyId: product.companyId,
        quantity: Number(quantity),
      };
      addToCart(productWithQuantity);
      toast.success(t("buyer_pages.product_details.added"), {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    }
  };

  const handleInquiryToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inInquiry) {
      removeFromInquiry(product.id);
    } else {
      const productWithQuantity = {
        ...product,
        quantity: product.quantity || product.minOrder,
      };
      addToInquiry(productWithQuantity);
    }
  };

  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(product.id);
    setIsFavorite(!isFavorite);
  };

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
  };

  const renderImages = () => {
    if (product.images.length === 1) {
      return (
        <div className="relative overflow-hidden flex justify-center items-center rounded-t-xl min-h-[200px]">
          <img
            src={product.images[0]}
            alt={`${product?.name} - Image 1`}
            className="w-full h-full object-cover transition-transform duration-300"
          />
        </div>
      );
    }

    return (
      <div className="relative overflow-hidden rounded-t-xl min-h-[200px]">
        <Slider arrows={false} {...sliderSettings}>
          {product.images.map((image, index) => (
            <div key={image} className="min-h-[200px]">
              <img
                src={image}
                alt={`${product?.name} - Image ${index + 1}`}
                className="w-full h-full min-h-[200px] object-cover"
              />
            </div>
          ))}
        </Slider>
      </div>
    );
  };

  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  if (viewMode === "list") {
    return (
      <Card
        className="overflow-hidden cursor-pointer transition-all duration-300 mb-4 border-0 bg-white/50 backdrop-blur-sm"
        onClick={handleCardClick}
      >
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-48 relative">
            {renderImages()}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 bg-white/90 hover:bg-white h-8 w-8 rounded-full shadow-lg backdrop-blur-sm"
              onClick={handleFavoriteToggle}
            >
              <BiHeart
                className={`w-4 h-4 transition-colors duration-200 ${
                  isFavorite ? "text-red-500 fill-red-500" : "text-gray-500"
                }`}
              />
            </Button>
          </div>
          <CardContent className="p-4 md:p-6 flex-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full">
                <FaStore className="text-blue-600 w-3 h-3" />
                <span className="text-xs text-blue-700 font-medium">
                  {product?.store?.name}
                </span>
              </div>
            </div>
            <h3 className="text-base md:text-lg font-semibold mb-3 text-gray-800 line-clamp-2">
              {product?.name}
            </h3>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-500 mb-2">
                  {t("supplier_sidebar.min_order")}: {product?.minOrder}{" "}
                  {product?.unit}s
                </p>
                <p className="text-xl md:text-2xl font-bold text-black mb-3">
                  {product?.price.toFixed(2)} {t("currency")}/{product?.unit}
                </p>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 md:h-9 w-8 md:w-9 rounded-lg border-gray-200 hover:border-emerald-500 hover:bg-emerald-50"
                    onClick={handleDecrement}
                    disabled={
                      quantity <=
                        (product.minOrder == 0 ? 1 : product.minOrder) ||
                      isInCart
                    }
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    min={product.minOrder == 0 ? 1 : product.minOrder}
                    value={quantity}
                    onChange={handleQuantityChange}
                    className="w-16 md:w-24 h-8 md:h-9 text-xs md:text-sm text-center border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                    disabled={isInCart}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value);
                      if (isNaN(value) || value < product.minOrder) {
                        setQuantity(product.minOrder);
                        if (isInCart) {
                          updateQuantity(product.id, product.minOrder);
                        }
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 md:h-9 w-8 md:w-9 rounded-lg border-gray-200 hover:border-emerald-500 hover:bg-emerald-50"
                    onClick={handleIncrement}
                    disabled={isInCart}
                  >
                    +
                  </Button>
                  <span className="text-xs text-gray-500">{product.unit}s</span>
                  <div className="ml-0 md:ml-4 text-base md:text-lg font-bold text-emerald-600">
                    {(product.price * quantity).toFixed(2)} {t("currency")}
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Tooltip
                  title={inInquiry ? "Remove from Inquiry" : "Add to Inquiry"}
                >
                  <Button
                    size="icon"
                    className={`h-10 w-10 md:h-12 md:w-12 rounded-full shadow-lg transition-all duration-200 ${
                      inInquiry
                        ? "bg-green-500 hover:bg-green-600 text-white"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                    }`}
                    onClick={handleInquiryToggle}
                  >
                    <TbTargetArrow className="w-5 h-5" />
                  </Button>
                </Tooltip>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-10 w-10 md:h-12 md:w-12 rounded-full shadow-lg transition-all duration-200 ${
                    isInCart
                      ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                  }`}
                  onClick={handleCartToggle}
                >
                  {isInCart ? (
                    <BsFillBasketFill className="w-5 h-5" />
                  ) : (
                    <BiBasket className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </div>
      </Card>
    );
  }
  return (
    <Card
      className="overflow-hidden w-full bg-white cursor-pointer transition-all duration-300 h-full border-0 rounded-2xl group"
      onClick={handleCardClick}
    >
      <div className="relative">
        {renderImages()}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full hover:bg-white backdrop-blur-sm transition-all duration-200"
            onClick={handleFavoriteToggle}
          >
            <BiHeart
              className={`w-4 h-4 transition-colors duration-200 ${
                isFavorite ? "text-red-500 fill-red-500" : "text-gray-500"
              }`}
            />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 rounded-full backdrop-blur-sm transition-all duration-200 ${
              isInCart
                ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                : "bg-white/90 hover:bg-white text-gray-500"
            }`}
            onClick={handleCartToggle}
          >
            {isInCart ? (
              <BsFillBasketFill className="w-4 h-4" />
            ) : (
              <BiBasket className="w-4 h-4" />
            )}
          </Button>
          <Button
            size="icon"
            className={`h-8 w-8 rounded-full backdrop-blur-sm transition-all duration-200 ${
              inInquiry
                ? "bg-green-500 hover:bg-green-600 text-white"
                : "bg-white/90 hover:bg-white text-gray-500"
            }`}
            onClick={handleInquiryToggle}
          >
            <TbTargetArrow className="w-4 h-4" />
          </Button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-1 px-2 py-1 bg-white/90 rounded-full backdrop-blur-sm">
              <FaStore className="text-purple-600 w-3 h-3" />
              <span className="text-xs text-purple-700 font-medium">
                {product.store?.name}
              </span>
            </div>
          </div>
        </div>
      </div>
      <CardContent className="p-4 border">
        <p className="font-bold text-[10px] text-black mb-2 flex items-center gap-1">
          5 <Star className="w-3 h-3 text-yellow-400" />{" "}
          <span className="text-gray-500">(50)</span>
        </p>
        <h3 className="text-sm font-semibold mb-2 text-gray-800 line-clamp-2 h-10">
          {product?.name}
        </h3>
        <div className="space-y-3">
          <div>
            <p className="font-bold text-black mb-2">
              {product.price.toFixed(2)} {t("currency")}/{product.unit}
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                disabled={
                  quantity <= (product.minOrder == 0 ? 1 : product.minOrder) ||
                  isInCart
                }
                className={cn(
                  "h-8 w-8 rounded-lg border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all duration-200",
                  (quantity <= (product.minOrder == 0 ? 1 : product.minOrder) ||
                    isInCart) &&
                    "text-gray-400"
                )}
                onClick={handleDecrement}
              >
                -
              </Button>
              <Input
                type="number"
                min={product.minOrder == 0 ? 1 : product.minOrder}
                value={quantity}
                onClick={(e) => e.stopPropagation()}
                onChange={handleQuantityChange}
                className="w-16 h-8 text-xs text-center border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                disabled={isInCart}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || value < product.minOrder) {
                    setQuantity(product.minOrder);
                    if (isInCart) {
                      updateQuantity(product.id, product.minOrder);
                    }
                  }
                }}
              />
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-lg border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all duration-200"
                onClick={handleIncrement}
                disabled={isInCart}
              >
                +
              </Button>
              <span className="text-xs hidden md:block text-gray-500">
                {product.unit}s
              </span>
            </div>
          </div>
          <div className="pt-2 border-t border-gray-100">
            <div className="text-sm font-bold">
              {t("buyer_pages.cart.total")}:{" "}
              {(product.price * quantity).toFixed(2)} {t("currency")}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ProductCard;
